/**
 * Error Boundary - Captura erros em componentes React
 * Previne que a aplicação inteira quebre quando um componente falha
 */

import React from 'react';
import ErrorFallback from './ErrorFallback';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Atualiza o state para mostrar a UI de fallback
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Salvar informações do erro
    this.setState({ errorInfo });

    // Log do erro
    console.error('[ErrorBoundary] Erro capturado:', error);
    console.error('[ErrorBoundary] Component Stack:', errorInfo?.componentStack);

    // Enviar para serviço de logs (se disponível)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = async (error, errorInfo) => {
    try {
      // Import dinâmico para evitar dependência circular
      const { logError } = await import('../services/errorLogService');

      if (logError) {
        // Passar o error real como primeiro parâmetro, context como segundo
        logError(error, {
          type: 'react-component',
          component: 'ErrorBoundary',
          action: 'ComponentCrash',
          componentStack: errorInfo?.componentStack,
          url: window.location.href,
          level: 'error',
        });
      }
    } catch (logErr) {
      console.warn('[ErrorBoundary] Falha ao enviar log:', logErr);
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Renderizar UI de fallback
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * HOC para envolver componentes com Error Boundary
 * @param {React.Component} Component - Componente a ser protegido
 * @param {React.Component} FallbackComponent - Componente de fallback opcional
 */
export function withErrorBoundary(Component, FallbackComponent = null) {
  return function WrappedComponent(props) {
    return (
      <ErrorBoundary FallbackComponent={FallbackComponent}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
